import { Suspense, useEffect } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { Toaster, type ToastPosition } from "react-hot-toast";
import { BrowserRouter } from "react-router-dom";
import { RecoilRoot } from "recoil";
import "./App.css";
import LayoutBase from "./atoms-react/layout-base/LayoutBase.react";
import { Tooltip } from "./atoms-react/tooltip/Tooptip.react";
import { getCookie, getJwt, setJwt } from "./features/auth/utils";
import { isIOS } from "./features/partner/utils";
import { useListenForShortcuts } from "./features/utils/shortcuts";
import { getTheme, setTheme } from "./features/utils/theme";
import { GatewaysProvider } from "./flow/components/gateways/gateways-provider/index.react";
import { InitialRouter } from "./index.";
import { Provider } from "react-redux";
import { store } from "./redux/store";
import { isDevelopment } from "./config";
import { StickyFloat } from "./atoms-react/sticky-floating";
import { Base } from "./atoms-react/text/Text.react";
import { Button } from "./atoms-react/button/Button.react";
import { Themes } from "./types/theme";
import { useTranslation } from "react-i18next";
import * as Sentry from "@sentry/react";

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      //we disable globaly the focus refetch to avoid rerender issues we need to keep track of this changes if something hppens
      refetchOnWindowFocus: false
    }
  }
});

const toastOptions = {
  success: {
    position: "bottom-right" as ToastPosition
  },
  error: {
    position: "bottom-right",
    className: "!text-white !bg-red-500"
  }
};
function ErrorFallback() {
  const { t } = useTranslation(["pass"]);
  return (
    <div className="flex h-full w-full flex-row justify-center" role="alert">
      <div className="flex h-full flex-col justify-center" role="alert">
        <Base className="text-primary text-xl">{t("olkypass.general.error")}</Base>
        <Button
          size="md"
          theme={Themes.white}
          className="mt-2 bg-primary text-white hover:text-primary"
          onClick={async () => {
            await window.location.reload();
          }}
        >
          <div>{t("olkypass.general.reload")}</div>
        </Button>{" "}
      </div>
    </div>
  );
}
function App() {
  useListenForShortcuts();
  setTheme(getTheme());

  useEffect(() => {
    // @ts-expect-error Query client hack for Vue
    window.queryClient = queryClient;
    if (isIOS()) {
      const token = JSON.parse(getCookie("jwt_cookie") as string);
      if (token && !getJwt().token) {
        setJwt({
          token: token.token as string
        });
        const date = new Date();
        date.setTime(date.getTime());
        const expires = `; expires=  ${date.toUTCString()}`;
        document.cookie = `jwt_cookie=${expires};·path=/`;

        window.location.replace("/");
      }
    }
  }, []);

  return (
    <Provider store={store}>
      <Suspense>
        <Sentry.ErrorBoundary fallback={ErrorFallback}>
          <RecoilRoot>
            <BrowserRouter>
              <QueryClientProvider client={queryClient}>
                <ReactQueryDevtools initialIsOpen={false} />
                {/* @ts-expect-error Children wrong type */}
                <Toaster toastOptions={toastOptions} autoClose={5000} />
                <Tooltip />
                <GatewaysProvider>
                  {isDevelopment && <StickyFloat />}
                  <LayoutBase>
                    <InitialRouter />
                    <Tooltip />
                  </LayoutBase>
                </GatewaysProvider>
              </QueryClientProvider>
            </BrowserRouter>
          </RecoilRoot>
        </Sentry.ErrorBoundary>
      </Suspense>
    </Provider>
  );
}

export default App;
